import styled from 'styled-components'

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({ theme }) => theme.zIndex.modal};
  padding: ${({ theme }) => theme.spacing.medium};

  @media (max-width: 640px) {
    padding: 0;
    padding-top: 48px;
    align-items: stretch;
  }
`

export const ModalContent = styled.div<{ $hasPDF?: boolean }>`
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  max-width: ${({ $hasPDF }) => ($hasPDF ? '1100px' : '700px')};
  width: 100%;
  max-height: 85vh;
  display: ${({ $hasPDF }) => ($hasPDF ? 'grid' : 'block')};
  grid-template-columns: ${({ $hasPDF }) => ($hasPDF ? '420px 1fr' : 'none')};
  grid-template-rows: ${({ $hasPDF }) => ($hasPDF ? 'auto 1fr' : 'none')};
  overflow-x: hidden;

  @media (max-width: 1024px) {
    max-width: 95vw;
    grid-template-columns: ${({ $hasPDF }) => ($hasPDF ? '1fr' : 'none')};
    grid-template-rows: ${({ $hasPDF }) =>
      $hasPDF ? 'auto auto 1fr' : 'none'};
  }

  @media (max-width: 640px) {
    max-width: 100%;
    max-height: 100%;
    border-radius: 0;
    overflow-y: auto;
    display: block;
  }
`

export const ModalHeader = styled.div<{ $hasPDF?: boolean }>`
  display: flex;
  flex-direction: column;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  padding: ${({ theme }) => theme.spacing.large};
  grid-column: ${({ $hasPDF }) => ($hasPDF ? '1 / -1' : 'auto')};
  border-bottom: ${({ $hasPDF, theme }) =>
    $hasPDF ? `1px solid ${theme.colors.border.default}` : 'none'};

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing.default};
    margin-bottom: ${({ theme }) => theme.spacing.small};
  }
`

export const HeaderTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

export const HeaderContext = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.small};
  flex-wrap: wrap;
`

export const ContextChevron = styled.span`
  color: ${({ theme }) => theme.colors.border.default};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  flex-shrink: 0;
  &::after {
    content: '›';
  }
`

export const HeaderDivider = styled.div`
  width: 140px;
  height: 1px;
  background: ${({ theme }) => theme.colors.border.light};
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

export const DetailsPanel = styled.div<{ $hasPDF?: boolean }>`
  padding: ${({ theme }) => theme.spacing.large};
  overflow-y: auto;

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing.default};
  }
  background: ${({ theme }) => theme.colors.background.secondary}10;

  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border.default};
    border-radius: ${({ theme }) => theme.borderRadius.small};

    &:hover {
      background: ${({ theme }) => theme.colors.text.tertiary};
    }
  }
`

export const PDFViewerPanel = styled.div`
  height: 100%;
  min-height: 500px;
  background: ${({ theme }) => theme.colors.background.primary};
  border-left: 1px solid ${({ theme }) => theme.colors.border.default};
  overflow: hidden;

  @media (max-width: 1024px) {
    border-left: none;
    border-top: 1px solid ${({ theme }) => theme.colors.border.default};
  }

  @media (max-width: 640px) {
    min-height: 300px;
  }
`

export const GeoportalPanel = styled.div`
  position: relative;
  height: 100%;
  min-height: 500px;
  background: ${({ theme }) => theme.colors.background.primary};
  border-left: 1px solid ${({ theme }) => theme.colors.border.default};
  overflow: hidden;

  .mapboxgl-popup {
    z-index: 10;
  }

  .mapboxgl-ctrl-group {
    z-index: 5;
  }

  @media (max-width: 1024px) {
    border-left: none;
    border-top: 1px solid ${({ theme }) => theme.colors.border.default};
  }

  @media (max-width: 640px) {
    min-height: 300px;
  }
`

export const EmptyRightPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 500px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-left: 1px solid ${({ theme }) => theme.colors.border.default};
  color: ${({ theme }) => theme.colors.text.tertiary};
  gap: ${({ theme }) => theme.spacing.default};
  padding: ${({ theme }) => theme.spacing.large};
  text-align: center;

  @media (max-width: 1024px) {
    border-left: none;
    border-top: 1px solid ${({ theme }) => theme.colors.border.default};
    min-height: 200px;
  }

  @media (max-width: 640px) {
    min-height: 150px;
    padding: ${({ theme }) => theme.spacing.default};
  }
`

/* ── Manual Override Timeline Panel ─────────────────────────── */

export const ManualTimelinePanel = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.large};
  border-left: 1px solid ${({ theme }) => theme.colors.border.default};
  background: ${({ theme }) => theme.colors.background.primary};
  overflow-y: auto;

  @media (max-width: 1024px) {
    border-left: none;
    border-top: 1px solid ${({ theme }) => theme.colors.border.default};
  }

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing.default};
  }
`

export const TimelineTitle = styled.h4`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.large} 0;
`

export const TimelineStep = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.default};
  position: relative;
`

export const TimelineStepIndicator = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
`

export const TimelineStepCircle = styled.div<{
  $variant: 'default' | 'warning' | 'success'
}>`
  width: 28px;
  height: 28px;
  border-radius: ${({ theme }) => theme.borderRadius.circle};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  flex-shrink: 0;

  ${({ $variant, theme }) => {
    switch ($variant) {
      case 'warning':
        return `
          background: ${theme.colors.warningLight};
          border: 1.5px solid ${theme.colors.warning};
          color: ${theme.colors.warning};
        `
      case 'success':
        return `
          background: ${theme.colors.successLight};
          border: 1.5px solid ${theme.colors.success};
          color: ${theme.colors.success};
        `
      default:
        return `
          background: ${theme.colors.background.secondary};
          border: 1px solid ${theme.colors.border.default};
          color: ${theme.colors.text.tertiary};
        `
    }
  }}
`

export const TimelineConnector = styled.div`
  width: 1.5px;
  height: 20px;
  background: ${({ theme }) => theme.colors.border.default};
  margin: ${({ theme }) => theme.spacing.xsmall} 0;
`

export const TimelineStepContent = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.small};
`

export const TimelineStepTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.4;
`

export const TimelineStepDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 2px;
  line-height: 1.4;
`

export const TimelineHighlightBox = styled.div`
  background: ${({ theme }) => theme.colors.warningLight};
  border: 1px solid ${({ theme }) => theme.colors.warning}33;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  margin-top: ${({ theme }) => theme.spacing.xsmall};
`

export const TimelineHighlightTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: #7a5a00;
`

export const TimelineHighlightDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: #9a7a20;
  margin-top: 2px;
`

export const EmptyIcon = styled.div`
  font-size: 48px;
  opacity: 0.4;
`

export const EmptyText = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  max-width: 260px;
  line-height: 1.5;
`

export const ModalTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.title};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;

  @media (max-width: 640px) {
    font-size: ${({ theme }) => theme.typography.fontSize.section};
  }
`

export const CloseButton = styled.button`
  background: none;
  border: none;
  padding: ${({ theme }) => theme.spacing.xsmall};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: color ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`

export const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

export const SectionTitle = styled.h4`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.small} 0;
`

export const ReferenceList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`

export const ReferenceItem = styled.li`
  padding: ${({ theme }) => `${theme.spacing.xsmall} 0`};
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  font-size: ${({ theme }) => theme.typography.fontSize.body};

  strong {
    min-width: 120px;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    flex-shrink: 0;
  }

  span {
    color: ${({ theme }) => theme.colors.text.primary};
    word-break: break-word;
    overflow-wrap: break-word;
    min-width: 0;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 2px;
    font-size: ${({ theme }) => theme.typography.fontSize.small};

    strong {
      min-width: unset;
      font-size: ${({ theme }) => theme.typography.fontSize.small};
    }
  }
`

export const DocumentLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  transition: color ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.primaryHover};
    text-decoration: underline;
  }
`

export const AdditionalInfo = styled.div`
  padding: ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.background.tertiary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.6;
  font-style: italic;
`

export const ValueHero = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.large};
  background: ${({ theme }) => theme.colors.background.secondary}10;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin-bottom: ${({ theme }) => theme.spacing.medium};

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing.default};
  }
`

export const HeroLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.small};
`

export const HeroValue = styled.div<{
  $isOverride?: boolean
  $compact?: boolean
}>`
  font-size: ${({ $compact }) => ($compact ? '15px' : '28px')};
  font-weight: ${({ theme, $compact }) =>
    $compact
      ? theme.typography.fontWeight.medium
      : theme.typography.fontWeight.bold};
  font-family: ${({ theme, $compact }) =>
    $compact
      ? theme.typography.fontFamily.sans
      : theme.typography.fontFamily.mono};
  line-height: ${({ $compact }) => ($compact ? 1.45 : 1.2)};
  color: ${({ $isOverride }) => ($isOverride ? '#e6a200' : 'inherit')};
  word-break: break-word;
  overflow-wrap: break-word;

  @media (max-width: 640px) {
    font-size: ${({ $compact }) => ($compact ? '14px' : '22px')};
  }
`

export const HeroUnit = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-left: 4px;
`

export const StatusBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.default};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  background: #fff3e0;
  color: #cc7700;
`

export const TabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid ${({ theme }) => theme.colors.border.light};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
`

export const Tab = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.text.primary : theme.colors.text.secondary};
  cursor: pointer;
  border: none;
  background: none;
  border-bottom: 2px solid
    ${({ $active }) => ($active ? '#1a1a1a' : 'transparent')};
  margin-bottom: -2px;
  white-space: nowrap;
  min-height: 36px;
`

export const TabValue = styled.span<{ $struck?: boolean; $color?: string }>`
  font-family: ${({ theme }) => theme.typography.fontFamily.mono};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  text-decoration: ${({ $struck }) => ($struck ? 'line-through' : 'none')};
  color: ${({ $color }) => $color || 'inherit'};
`

export const WarningBox = styled.div<{
  $type: 'alg' | 'reg' | 'geo' | 'conflict'
}>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.medium};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  line-height: 1.5;
  background: ${({ $type }) => ($type === 'conflict' ? '#fff5f5' : '#fff8e1')};
  border: 1px solid
    ${({ $type }) => ($type === 'conflict' ? '#fecaca' : '#ffe082')};
  color: ${({ $type }) => ($type === 'conflict' ? '#991b1b' : '#6d4c00')};

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing.small};
  }
`

export const WarningContent = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  align-items: flex-start;
`

export const ReportLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  align-self: flex-start;

  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.colors.primaryHover};
  }
`

export const GeoportalButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  border: 1px solid #d4c8e8;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  background: #faf7fd;
  color: #7733bb;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  text-decoration: none;
  cursor: pointer;

  &:hover {
    background: #f0e8fa;
  }
`

export const OverrideDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.6;
`

export const ReportFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
`

export const ReportContextBlock = styled.div`
  padding: ${({ theme }) => theme.spacing.default};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
`

export const ReportContextRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${({ theme }) => `${theme.spacing.xsmall} 0`};

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  }
`

export const ReportContextLabel = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`

export const ReportContextValue = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`

export const ReportInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background.primary};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.border.focus};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`

export const ReportTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background.primary};
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.border.focus};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`

export const ReportFieldLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xsmall};
`

export const ReportButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing.small};

  @media (max-width: 640px) {
    flex-direction: column;
  }
`

export const ReportSubmitButton = styled.button<{ $loading?: boolean }>`
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.large};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: ${({ $loading }) => ($loading ? 'not-allowed' : 'pointer')};
  opacity: ${({ $loading }) => ($loading ? 0.7 : 1)};
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primaryHover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const ReportCancelButton = styled.button`
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.large};
  background: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`

export const ReportSuccessMessage = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.successLight};
  color: ${({ theme }) => theme.colors.success};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`

export const ReportBoundSelector = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
`

export const ReportBoundOption = styled.button<{ $selected: boolean }>`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  background: ${({ $selected, theme }) =>
    $selected
      ? theme.colors.primaryLight || 'rgba(0, 122, 255, 0.08)'
      : theme.colors.background.primary};
  color: ${({ $selected, theme }) =>
    $selected ? theme.colors.primary : theme.colors.text.secondary};
  border: 1.5px solid
    ${({ $selected, theme }) =>
      $selected ? theme.colors.primary : theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`

/* ── Envelope calculation breakdown (Regelbauweise) ─────────────── */

type CalcTone = 'result' | 'total' | 'warn' | 'muted' | 'negative' | undefined

export const CalcList = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
`

export const CalcRow = styled.div<{ $tone?: CalcTone }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.medium}`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  font-size: ${({ theme }) => theme.typography.fontSize.small};

  &:last-child {
    border-bottom: none;
  }

  background: ${({ $tone, theme }) => {
    switch ($tone) {
      case 'result':
        return theme.colors.primaryLight
      case 'total':
        return theme.colors.successLight
      case 'warn':
        return theme.colors.background.secondary
      case 'negative':
        return theme.colors.background.secondary
      default:
        return theme.colors.background.primary
    }
  }};
`

export const CalcOp = styled.span`
  width: 16px;
  flex-shrink: 0;
  text-align: center;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const CalcLabelWrap = styled.div`
  flex: 1;
  min-width: 0;
`

export const CalcLabel = styled.div<{ $tone?: CalcTone }>`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ $tone, theme }) => {
    switch ($tone) {
      case 'warn':
        return '#CC6B00'
      case 'negative':
        return theme.colors.danger
      case 'muted':
        return theme.colors.text.tertiary
      default:
        return theme.colors.text.primary
    }
  }};
`

export const CalcSub = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const CalcValue = styled.span<{ $tone?: CalcTone }>`
  white-space: nowrap;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ $tone, theme }) => {
    switch ($tone) {
      case 'total':
        return '#008A2E'
      case 'negative':
        return theme.colors.danger
      case 'warn':
        return '#CC6B00'
      case 'muted':
        return theme.colors.text.tertiary
      case 'result':
        return theme.colors.primary
      default:
        return theme.colors.text.primary
    }
  }};
`

export const ThreeDChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px ${({ theme }) => theme.spacing.xsmall};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  background: ${({ theme }) => theme.colors.background.secondary};
  color: ${({ theme }) => theme.colors.text.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
`

/* ── Reference cards (multi-reference lists, e.g. envelope inputs) ── */

export const ReferenceCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.medium}`};
  margin-bottom: ${({ theme }) => theme.spacing.small};

  &:last-child {
    margin-bottom: 0;
  }
`

export const ReferenceCardTitle = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
  padding-bottom: ${({ theme }) => theme.spacing.xsmall};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
`
